import {
  useGetChurnRateHistoryQuery,
  useGetDropoutHistoryQuery,
  useGetRatioHistoryQuery,
  useGetRetentionHistoryQuery,
  useGetShowRateHistoryQuery,
} from 'gql/types';
import { useMemo } from 'react';

import { IRecruiterDashboardSettings } from '../../../hooks/useRecruiterDashboardSettings';
import { STATUS_COLORS } from '../constants';
import { TrendDirection } from '../types';
import { IRecruiterFilters } from '../utils/recruiterFilters';

export interface IMetricTrends {
  ratioTrend: TrendDirection;
  ratioTrendColor: string;
  churnTrend: TrendDirection;
  churnTrendColor: string;
  churnQuarterlyTrend: TrendDirection;
  churnQuarterlyTrendColor: string;
  showRateTrend: TrendDirection;
  showRateTrendColor: string;
  retentionTrend: TrendDirection;
  retentionTrendColor: string;
  dropoutTrend: TrendDirection;
  dropoutTrendColor: string;
}

function computeTrend(
  prev: number | undefined,
  curr: number | undefined,
  threshold = 1
): TrendDirection {
  if (prev == null || curr == null) return 'flat';
  if (curr > prev + threshold) return 'up';
  if (curr < prev - threshold) return 'down';
  return 'flat';
}

function goodUpColor(t: TrendDirection): string {
  return t === 'up'
    ? STATUS_COLORS.good.fill
    : t === 'down'
    ? STATUS_COLORS.poor.fill
    : STATUS_COLORS.ok.fill;
}

function goodDownColor(t: TrendDirection): string {
  return t === 'up'
    ? STATUS_COLORS.poor.fill
    : t === 'down'
    ? STATUS_COLORS.good.fill
    : STATUS_COLORS.ok.fill;
}

export function useMetricTrends(
  filters: IRecruiterFilters,
  dateBoundsStart: Date,
  dateBoundsEnd: Date,
  settings: IRecruiterDashboardSettings | null
): IMetricTrends {
  const trendPeriodType = useMemo(() => {
    const dr = filters.dateRange;
    if (
      dr === 'day' ||
      dr === 'yesterday' ||
      dr === 'dayCal' ||
      dr === 'yesterdayCal'
    )
      return 'dayCal';
    if (
      dr === 'week' ||
      dr === 'lastWeek' ||
      dr === 'weekCal' ||
      dr === 'lastWeekCal'
    )
      return 'weekCal';
    if (
      dr === 'quarter' ||
      dr === 'lastQuarter' ||
      dr === 'quarterCal' ||
      dr === 'lastQuarterCal'
    )
      return 'quarterCal';
    return 'monthCal';
  }, [filters.dateRange]);

  const trendAnchorRange = useMemo(
    () => ({ start: dateBoundsStart, end: dateBoundsEnd }),
    [dateBoundsStart.getTime(), dateBoundsEnd.getTime()]
  );

  const trendFilters = useMemo(
    () => ({
      regionIds: filters.regionIds.length ? filters.regionIds : undefined,
      officeIds: filters.officeIds.length ? filters.officeIds : undefined,
      locationIds: filters.locationIds.length ? filters.locationIds : undefined,
      activeDonorStatuses: settings?.activeDonorStatuses?.length
        ? settings.activeDonorStatuses
        : undefined,
    }),
    [
      filters.regionIds,
      filters.officeIds,
      filters.locationIds,
      settings?.activeDonorStatuses,
    ]
  );

  const skip = !settings;

  const { data: ratioHistData } = useGetRatioHistoryQuery({
    variables: {
      input: {
        periodType: trendPeriodType,
        bars: 2,
        anchorRange: trendAnchorRange,
        ...trendFilters,
        repeatDonationsThreshold: settings?.repeatDonationsThreshold,
      },
    },
    skip,
  });

  const { data: churnHistData } = useGetChurnRateHistoryQuery({
    variables: {
      input: {
        periodType: trendPeriodType,
        bars: 2,
        anchorRange: trendAnchorRange,
        ...trendFilters,
      },
    },
    skip,
  });

  const { data: churnQuarterlyHistData } = useGetChurnRateHistoryQuery({
    variables: {
      input: {
        periodType: 'quarterCal',
        bars: 2,
        anchorRange: trendAnchorRange,
        ...trendFilters,
      },
    },
    skip,
  });

  const { data: showRateHistData } = useGetShowRateHistoryQuery({
    variables: {
      input: {
        periodType: trendPeriodType,
        bars: 2,
        anchorRange: trendAnchorRange,
        ...trendFilters,
        donorDonationFrequency: settings?.donorDonationFrequency,
        donorEligibilityDays: settings?.donorEligibilityDays,
      },
    },
    skip,
  });

  const { data: retentionHistData } = useGetRetentionHistoryQuery({
    variables: {
      input: {
        periodType: trendPeriodType,
        bars: 2,
        anchorRange: trendAnchorRange,
        ...trendFilters,
        repeatDonationsThreshold: settings?.repeatDonationsThreshold,
      },
    },
    skip,
  });

  const { data: dropoutHistData } = useGetDropoutHistoryQuery({
    variables: {
      input: {
        periodType: 'quarterCal',
        bars: 2,
        anchorRange: trendAnchorRange,
        ...trendFilters,
      },
    },
    skip,
  });

  const ratioPeriods = ratioHistData?.getRatioHistory?.periods ?? [];
  const ratioTrend = computeTrend(
    ratioPeriods[0]?.firstTimePct,
    ratioPeriods[1]?.firstTimePct
  );

  const churnPeriods = churnHistData?.getChurnRateHistory?.periods ?? [];
  const churnTrend = computeTrend(
    churnPeriods[0]?.churnRate,
    churnPeriods[1]?.churnRate
  );

  const churnQPeriods =
    churnQuarterlyHistData?.getChurnRateHistory?.periods ?? [];
  const churnQuarterlyTrend = computeTrend(
    churnQPeriods[0]?.churnRate,
    churnQPeriods[1]?.churnRate
  );

  const showRatePeriods = showRateHistData?.getShowRateHistory?.periods ?? [];
  const showRateTrend = computeTrend(
    showRatePeriods[0]?.showRate,
    showRatePeriods[1]?.showRate
  );

  const retentionPeriods =
    retentionHistData?.getRetentionHistory?.periods ?? [];
  const retentionTrend = computeTrend(
    retentionPeriods[0]?.retentionScore,
    retentionPeriods[1]?.retentionScore
  );

  const dropoutPeriods = dropoutHistData?.getDropoutHistory?.periods ?? [];
  const dropoutTrend = computeTrend(
    dropoutPeriods[0]?.dropoutRate,
    dropoutPeriods[1]?.dropoutRate
  );

  return {
    // ratio: UP = more first-timers = bad
    ratioTrend,
    ratioTrendColor: goodDownColor(ratioTrend),
    // churn: UP = more churn = bad
    churnTrend,
    churnTrendColor: goodDownColor(churnTrend),
    churnQuarterlyTrend,
    churnQuarterlyTrendColor: goodDownColor(churnQuarterlyTrend),
    // show rate: UP = more show-ups = good
    showRateTrend,
    showRateTrendColor: goodUpColor(showRateTrend),
    // retention: UP = better retention = good
    retentionTrend,
    retentionTrendColor: goodUpColor(retentionTrend),
    // dropout: UP = more dropouts = bad
    dropoutTrend,
    dropoutTrendColor: goodDownColor(dropoutTrend),
  };
}
